import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { DatePipe, formatDate, formatNumber } from '@angular/common';
import { Title } from "@angular/platform-browser";
import { async, Observable, throwError, concat, of, from, Subscription } from 'rxjs';
import { map, switchMap, concatMap } from 'rxjs/operators';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { Product, TurnoverRow } from 'src/app/shared/product'
import { ProductApiService } from 'src/app/services/product-api.service';
import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

@Component({
  selector: 'app-item-turnover-sheet',
  templateUrl: './item-turnover-sheet.component.html',
  styleUrls: ['./item-turnover-sheet.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Standalone item turnover report page for a single product.
 *
 * Reads `_prodId` from route params and loads the product via `getProductById`. Turnover
 * data is received from a child component via `onTurnoverTableReady`; the selected document
 * ID is received via `onTurnoverDocument`.
 *
 * `onReloadAllData` re-spreads `product` to force the child component to refresh without
 * a full route reload.
 *
 * Note: the class declaration implements `OnInit` only, though `ngOnDestroy` exists and
 * unsubscribes the route-param and product queries.
 */
export class ItemTurnoverSheetComponent implements OnInit {

  globalSettinsData: GlobalSettingsData;

  /*private*/ _prodId: number;

  allowComplex: boolean = true;
  allowOld: boolean = true;

  product: Product;
  private _paramQuery: Subscription;
  private _productQuery: Subscription;

  turnOver: TurnoverRow[];

  documentId: number;

  depth: number = 6;

  /** `true` when no turnover document has been selected. */
  public get ifThereNotSelection(): boolean { return this.documentId ? false : true; }
  /** Always `false` — placeholder for future parent-navigation logic. */
  public get ifSelectedHasNotParent(): boolean { return false; }

  /** `true` when the turnover data array is non-empty. */
  public get tableIsReady(): boolean { return (this.turnOver || []).length > 0; }

  constructor(
    private route: ActivatedRoute,
    private prodApi: ProductApiService,
    private router: Router,
    private titleService: Title,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  /** Unsubscribes the route-param and product queries. */
  ngOnDestroy(): void {
    console.log('*** ItemTurnoverSheetComponent in ngOnDestroy')

    if (this._paramQuery) { this._paramQuery.unsubscribe(); }
    if (this._productQuery) { this._productQuery.unsubscribe(); }
  }

  /** Subscribes to route params, loads the product, and sets the browser title. */
  ngOnInit(): void {
    console.log('*** ItemTurnoverSheetComponent in ngOnInit')

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this._paramQuery =
      this.route.paramMap
        .subscribe((params: ParamMap) => {
          this._prodId = +params.get('id');
          console.log('*** ItemTurnoverSheetComponent in param subscribe: (' + this._prodId + ')');

          this._productQuery =
            this.prodApi.getProductById(this._prodId)
              .pipe(map(data => this.product = data[0]))
              .subscribe();

        });
    this.titleService.setTitle('Оборотная ведомость по товару');
  }

  /** Navigates to the turnover report for the newly selected product. */
  onProductSelection(event) {
    console.log('*** ItemTurnoverSheetComponent in onProductSelection')
    this.router.navigate(['/show-itemturnoverreport', event.PRODID]);
  }

  /** Placeholder for document-tree navigation (no-op). */
  showDoctreeButtonClick(event) {
    console.log('*** ItemTurnoverSheetComponent in onProductSelection')
  }

  /** Placeholder for parent-document navigation (no-op). */
  moveToParentDocumentBtn(event) {
    console.log('*** ItemTurnoverSheetComponent in onProductSelection')
  }

  /** Re-spreads `product` to trigger child component refresh. */
  onReloadAllData(event) {
    console.log('*** ItemTurnoverSheetComponent in onReloadAllData')

    this.product = {
      ... this.product
    };

    // this.router.navigate(['/show-itemturnoverreport', this._prodId]);
  }

  /** Writes a Blob to an `.xlsx` file via `file-saver`. */
  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /** Exports the current turnover rows to an xlsx file. */
  onExportData() {
    console.log('*** ItemTurnoverSheetComponent onExportData: ')

    import("xlsx").then(xlsx => {

      let exportData: any =
        this.turnOver.map(e => ({
          'Документ': e.DOCNAME,
          'Номер': e.DOCNUMBER,
          'Дата': new Date(e.DOCDATE),
          'Покупатель': e.CLINAME,
          'Склад': e.DEPNAME,
          'Цена в валюте': e.VPRICE,
          'Курс': e.CRATE,
          'Кол-во до': e.STOCKBE,
          'Дебет': e.INCOME,
          'Кредит': e.OUTCOME,
          'Кол-во после': e.STOCKBE,
          'Юрлицо': e.LOCCOMP,
          'До': e.LCSTOCKBE,
          'После': e.LCSTOCKAF,
          'Примечание продавца': e.DOCNOTE1,
          'Примечание покупателя': e.DOCNOTE2,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { turnover: worksheet }, SheetNames: ['turnover'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "turnover");
    });
  }

  /** Receives the selected document ID from the child turnover table. */
  onTurnoverDocument(event) {
    console.log('*** ItemTurnoverSheetComponent in onTurnoverDocument' + JSON.stringify(event))

    this.documentId = event;
  }

  /** Receives the complete turnover data array from the child component. */
  onTurnoverTableReady(event) {
    //console.log('*** ItemTurnoverSheetComponent in onTurnoverTableReady' + JSON.stringify(event))

    this.turnOver = event;
  }

}
